import type { TimestampInMillis, UUID } from '@cobliteam/common-types';

export interface FuelCardMatching {
  id: UUID;
  fleetId: UUID;
  licensePlate?: UUID;
  fuelCardId: UUID;
  providerLicensePlate?: string;
  updatedAt: TimestampInMillis;
  createdAt: TimestampInMillis;
  createdBy: string;
}

export interface ZendeskTicket {
  problem: string;
  message: string;
  fleetName: string;
  fleetId: string;
  userName: string;
  userEmail: string;
}

export enum CardProviderOption {
  ALELO = 'VeloeGo',
  WIZEO = 'Wizeo',
  MAXIFROTA = 'Maxifrota',
  TICKETLOG = 'Ticketlog',
  CTASMART = 'CTASmart',
  VOLUS = 'Volus',
  GET_TO_KNOW = 'get_to_know',
  OTHER = 'other',
}

/**
 * One partner row of `GET /fuel/card-providers` (FND-06). `slug` is the stable key: the internal
 * name is a different word for some partners (Veloe Go is stored as `Alelo`).
 */
export interface CardProvider {
  slug: string;
  displayName: string;
  logoKey: string;
  requiresKeyPerCnpj: boolean;
}

export const FLEET_FUEL_INTEGRATION_STATE_ACTIVE = 'active';
export const FLEET_FUEL_INTEGRATION_STATE_REQUESTED = 'requested';

export type FleetFuelIntegrationState = typeof FLEET_FUEL_INTEGRATION_STATE_ACTIVE | typeof FLEET_FUEL_INTEGRATION_STATE_REQUESTED;

export interface FleetFuelIntegrationPartner {
  slug: string;
  displayName: string;
  logoKey: string;
}

/**
 * One row of what the fleet has with a partner, as `E1` and `T1` read it (FND-07,
 * `GET /fuel/integrations`). Mirrors `FleetFuelIntegrationResponse` on the backend — `cnpj`,
 * `requestedByName`, `integrationId`, `requestCnpjId` and `lastOutcome` are all nullable there for
 * reasons specific to each field (predates the CNPJ registry, predates FND-05, row is a request
 * without an integration yet, or vice-versa), not a single shared "optional" convention.
 */
export interface FleetFuelIntegration {
  provider: FleetFuelIntegrationPartner;
  cnpj: string | null;
  state: FleetFuelIntegrationState;
  stateDate: string;
  requestedByName: string | null;
  integrationId: string | null;
  requestCnpjId: string | null;
  lastOutcome: string | null;
}

/** One person named in a `POST /fuel/integration-requests` body (T6a/T6b, FND-08). */
export interface IntegrationRequestPerson {
  name: string;
  email: string;
  role?: string;
  phone?: string;
}

/** One CNPJ entry of a `POST /fuel/integration-requests` body — company name is customer-typed. */
export interface IntegrationRequestCnpj {
  cnpj: string;
  companyName: string;
}

/** The consent T7 records alongside the request. `ip`/`location` are not client-supplied — see G8. */
export interface IntegrationRequestConsent {
  textVersion: string;
  pageUrl: string;
}

export interface CreateIntegrationRequestBody {
  providerSlug: string;
  cnpjs: IntegrationRequestCnpj[];
  requester: IntegrationRequestPerson;
  decider: IntegrationRequestPerson;
  consent: IntegrationRequestConsent;
}

export type FuelCardIntegrationRequestCnpjState = string;

export interface RequestedCnpj {
  id: string;
  cnpj: string;
  companyName: string;
  state: FuelCardIntegrationRequestCnpjState;
}

/**
 * What `POST /fuel/integration-requests` answers with (FND-08). `alreadyIntegrated` is Check B
 * (T5) — out of this task's scope (PNL-06/GF-830 owns showing it), kept here so the type matches
 * the real response instead of a partial guess.
 */
export interface IntegrationRequestResponse {
  requestId: string;
  integrationId: string | null;
  cnpjs: RequestedCnpj[];
  alreadyIntegrated: string[];
}

export interface FuelTransaction {
  id: string;
  transactionTime: string;
  cobliCostPerDrivenKm?: number;
  merchant: FuelMerchant;
  transactioner?: FuelTransactioner;
  driverName?: string;
  vehicle: FuelVehicle;
  createdAt: string;
  deletedAt?: string;
  odometer?: number;
  cobliOdometer?: number;
  creatorId?: string;
  updatedBy?: string;
  updatedAt?: string;
  deletedBy?: string;
  units: number;
  unitCost: number;
  totalCost: number;
  drivenMeters: number;
  cobliDrivenMeters?: number;
  fuelConsumption?: number;
  cobliFuelConsumption?: number;
  costPerDrivenKm?: number;
  providerCode?: string;
  commodity: string;
  createdBy: string;
  /** How the entry got in. Null on rows written before the column existed. */
  source?: string | null;
  /** Presigned URL of the fuel pump photo. Absent on entries created before the third slot existed. */
  fuelPumpFileUrl?: string | null;
  costCenterDescription?: string;
  costCenterId?: string;
  groupPath?: string;
  fileUrl: string | null;
  /** S3 URL of the odometer photo uploaded alongside the transaction. `null` when no photo was sent. */
  odometerFileUrl?: string | null;
}

export interface FuelMerchant {
  id: string;
  cnpj?: string;
  name?: string;
  city?: string;
  state?: string;
  lat?: string;
  lng?: string;
}

export interface FuelTransactioner {
  id: string;
  cpf: string;
  name: string;
}

export interface FuelVehicle {
  id: string;
  vehicleId: string;
  refFuelConsumption?: number;
  integration: FuelIntegration;
  licensePlate?: string;
  model?: string;
}

interface FuelIntegration {
  id: string;
  fleetId: string;
  createdAt: string;
  ignoreTransactionType: boolean;
}
